/**
 * Periods somebody keeps coming back to.
 *
 * Two kinds. **Saved** ones are named by the person: a relative preset stays
 * relative ("Last quarter" is always the last quarter), anything else keeps
 * its dates. **Anchored** ones come from the data — since the last signed
 * stock count, since the last closed month — and are worked out each time,
 * because the answer moves when the next count is signed.
 *
 * Saved periods are kept in this browser, per device. They are a convenience
 * for the person at this screen, not plant data, and losing them costs a
 * couple of clicks.
 */

import { useEffect, useState } from "react";
import { request } from "./api";
import { resolvePeriod, todayIso, type Period, type PeriodPreset } from "./period";

export interface SavedPeriod {
  id: string;
  name: string;
  period: Period;
}

const KEY = "neg.savedPeriods";
const MAX = 12;

function read(): SavedPeriod[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? (JSON.parse(raw) as SavedPeriod[]) : [];
    return Array.isArray(list) ? list.filter((s) => s?.period?.from) : [];
  } catch {
    return [];
  }
}

function write(list: SavedPeriod[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    // Private mode or full storage: the save simply does not stick.
  }
}

export function loadSaved(): SavedPeriod[] {
  return read();
}

export function savePeriod(name: string, period: Period): SavedPeriod[] {
  const entry: SavedPeriod = {
    id: `${Date.now()}`,
    name: name.trim().slice(0, 40),
    period,
  };
  const list = [entry, ...read().filter((s) => s.name !== entry.name)];
  write(list);
  return list.slice(0, MAX);
}

export function removeSaved(id: string): SavedPeriod[] {
  const list = read().filter((s) => s.id !== id);
  write(list);
  return list;
}

/** A saved relative preset resolves afresh; anything else keeps its dates. */
export function resolveSaved(saved: SavedPeriod, now: Date = new Date()): Period {
  const preset = saved.period.preset as PeriodPreset;
  if (preset !== "custom") return resolvePeriod(preset, now, saved.period);
  return saved.period;
}

// -- Anchors -----------------------------------------------------------------

interface Anchors {
  last_stock_count: string | null;
  last_closed_month: string | null;
}

export interface Anchor {
  key: string;
  label: string;
  period: Period;
}

let anchors: Promise<Anchors | null> | null = null;

function fetchAnchors(): Promise<Anchors | null> {
  // Once per session: a stock count is signed a few times a quarter.
  anchors ??= request<Anchors>("/api/reports/period-anchors").catch(() => null);
  return anchors;
}

function dayAfter(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return todayIso(new Date(y, m - 1, d + 1));
}

function monthEnd(isoDate: string): string {
  const [y, m] = isoDate.split("-").map(Number);
  return todayIso(new Date(y, m, 0));
}

/** "Since last stock count" and "Since last close", where the data has them. */
export function useAnchors(enabled: boolean): Anchor[] {
  const [list, setList] = useState<Anchor[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    fetchAnchors().then((a) => {
      if (!live || !a) return;
      const today = todayIso();
      const out: Anchor[] = [];
      if (a.last_stock_count && a.last_stock_count <= today) {
        out.push({
          key: "since_count",
          label: "Since last stock count",
          // The count is a balance at the end of its day; what moved since
          // starts the day after.
          period: { preset: "custom", from: minDate(dayAfter(a.last_stock_count), today), to: today },
        });
      }
      if (a.last_closed_month) {
        const from = dayAfter(monthEnd(a.last_closed_month));
        if (from <= today) {
          out.push({
            key: "since_close",
            label: "Since last close",
            period: { preset: "custom", from, to: today },
          });
        }
      }
      setList(out);
    });
    return () => {
      live = false;
    };
  }, [enabled]);
  return list;
}

function minDate(a: string, b: string): string {
  return a < b ? a : b;
}
